import { createStore } from 'vuex'
import axios from 'axios'
import folderClinic from './folderClinic'
import {
  IS_DEV_SERVER,
  resolveAuthApiBase,
  resolveLocalServerApiBase,
  resolveLocalServerHostBase,
} from "../apiConfig";
import {
  attachLocalServerAuthHeaders,
  getMemoryAuthToken,
  setMemoryAuthToken,
} from "../localServerAuth";

const localServerApiBase = resolveLocalServerApiBase();
const localServerHostBase = resolveLocalServerHostBase();
const authApiBase = resolveAuthApiBase();

const authHttp = axios.create({
  baseURL: authApiBase,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Legacy Worldish `http` client + hardcoded JWT removed (HEL-2280).

const userRecordInstance = axios.create({
  baseURL: IS_DEV_SERVER ? "" : localServerHostBase,
  withCredentials: true,
});

userRecordInstance.interceptors.request.use(attachLocalServerAuthHeaders);

const localServerHttp = axios.create({
  baseURL: localServerApiBase,
  withCredentials: true,
});
localServerHttp.interceptors.request.use(attachLocalServerAuthHeaders);


export default createStore({
  modules: {
    folderClinic
  },
  state: {
    clinics: [],
    userData: null,
    usersByLink: {}, // Map: linkId -> users array
    // adminId:"b43e9c54-1b8a-4c67-9f7f-a4ea0ae4b656",
    adminId:"",
    users:[],
    allUsers: [],   
    baseUrl: localServerApiBase,
    authUrl: authApiBase,
    folders: [],
    folderClinics: {},
    isSaving: false,
    categories: [],
    isSuperAdmin: false,
    // HEL-2250: access JWT kept in Vuex / memoryAuthToken only
    token: null,
    inactivityTimer: {
      started: false,
      timerId: null,
      onTimeout: null,
      startDate: null,
      maxTimer: 30 * 60, // 30 minutes
    },
  },
  mutations: {
    SET_CATEGORIES(state, categories) {
      state.categories = categories;
    },
    START_ACTIVITY_TIMER(state, { timerId, onTimeout }) {
      state.inactivityTimer.started = true;
      state.inactivityTimer.timerId = timerId;
      state.inactivityTimer.onTimeout = onTimeout;
      state.inactivityTimer.startDate = Date.now();
    },
    RESET_ACTIVITY_TIMER(state) {
      state.inactivityTimer.startDate = Date.now();
    },
    STOP_ACTIVITY_TIMER(state) {
      state.inactivityTimer.started = false;
      state.inactivityTimer.timerId = null;
      state.inactivityTimer.onTimeout = null;
      state.inactivityTimer.startDate = null;
    },
    SET_TOKEN(state, token) {
      state.token = token || null;
      setMemoryAuthToken(token);
      try {
        localStorage.removeItem("token");
      } catch (_) {
        /* ignore */
      }
    },
    SET_USER_DATA
    (state, payload) {
      state.userData = payload;
    },
    SET_IS_SUPER_ADMIN(state, value) {
      state.isSuperAdmin = value;
    },
    
    SET_CLINICS(state, clinics) {
    state.clinics = clinics;
  },

    SET_CLINIC(state, clinicsFromApi) {
  const oldMap = new Map(state.clinics.map(c => [c.id, c]));

  state.clinics = clinicsFromApi.map(item => {
    const old = oldMap.get(item.id) || {};
    return {
      id: item.id,
      clinicName: item.clinicName ?? item.name ?? "",
      clinicLinkId: item.clinicLinkId ?? item.link ?? null,
      adminLinkId: item.adminLinkId ?? old.adminLinkId ?? null,
      folderIds: Array.isArray(old.folderIds) ? [...old.folderIds] : [],
    };
  });

  // Persist after setting
  localStorage.setItem("clinics", JSON.stringify(state.clinics));
    },


    SET_USERS_BY_LINK
    (state, payload) {
      state.usersByLink = payload;
    },

    SET_USERS(state, users) {
      state.users = users
    },
    SET_ADMIN_LINK(state, id) {
    state.adminId = id;
    },
    SET_ALL_USERS(state, users) {
    state.allUsers = users
  },
  ADD_FOLDER(state, folder) {
    state.folders.push(folder);
  },
  ASSIGN_CLINIC_TO_FOLDER(state, { clinicId, folderId }) {
    const clinic = state.clinics.find(c => c.id === clinicId);
    if (clinic) {
      if (!Array.isArray(clinic.folderIds)) {
        clinic.folderIds = [];
      }
      if (!clinic.folderIds.includes(folderId)) {
        clinic.folderIds.push(folderId);
      }
    }

    // Save state after change
    localStorage.setItem("clinics", JSON.stringify(state.clinics));
    localStorage.setItem("folders", JSON.stringify(state.folders));
  },

  REMOVE_FOLDER(state, folderId) {
    state.folders = state.folders.filter(f => f.id !== folderId);

    // also remove folder references from clinics
    state.clinics.forEach(c => {
      if (Array.isArray(c.folderIds)) {
        c.folderIds = c.folderIds.filter(id => id !== folderId);
      }
    });

    // Save state after change
    localStorage.setItem("clinics", JSON.stringify(state.clinics));
    localStorage.setItem("folders", JSON.stringify(state.folders));
  },

  INIT_STATE(state) {
    const savedClinics = localStorage.getItem("clinics");
    const savedFolders = localStorage.getItem("folders");
    const savedIsSuperAdmin = localStorage.getItem("isSuperAdmin");
    if (savedClinics) state.clinics = JSON.parse(savedClinics);
    if (savedFolders) state.folders = JSON.parse(savedFolders);
    if (savedIsSuperAdmin) state.isSuperAdmin = savedIsSuperAdmin === "true";
  },

  },
  
  actions: {
 
    async fetchAllUsers({ commit, getters }) {
      try {
        const clinics = getters.getClinics;
        console.log("Clinics for fetching all users:", clinics);
        if (!clinics || clinics.length === 0) {
          commit("SET_ALL_USERS", []);
          return;
        }

        // array of promises (no await them )
        const allUserPromises = clinics.map(clinic => {
          return localServerHttp.get(
            `/userrecord/admin/getAllUserConfig?linkIds=${clinic.clinicLinkId}`
          ).then(response => {
            return response.data.map(user => ({
              id: user.id,
              email: user.email,
              active: user.active,
              clinicLinkId: clinic.clinicLinkId, 
            }));
          }).catch(err => {
            console.error(`Failed to fetch users for clinic ${clinic.clinicLinkId}:`, err);
            return []; // Return empty array for this clinic
          });
        });

        // Wait for ALL promises to finish in parallel
        const userArrays = await Promise.all(allUserPromises);

        const allUsers = userArrays.flat();

        commit("SET_ALL_USERS", allUsers);
      } catch (error) {
        console.error("Error fetching all users in parallel:", error);
        commit("SET_ALL_USERS", []);
      }
    },
     async fetchClinics({ commit, state }) {
  try {
    let adminLink = localStorage.getItem("link");
    // console.log("local admin link",localStorage.getItem("link"))
     const response = await localServerHttp.get(
          state.isSuperAdmin ? `/adminview/all` : `/clinic/${adminLink}`
        )
        // console.log("Clinic response", response.data);
    const clinics = response.data.map(item => ({
      id: item.id,
      name: item.clinicName,
      clinicLinkId: item.clinicLinkId,
      adminLinkId: item.adminLinkId
    }))
    // console.log("clinics",clinics)
    commit("SET_CLINICS", clinics)

    } catch (error) {
      console.error("Error fetching clinics:", error.response?.data || error)
    }
  },
    async fetchUsersByClinic({ commit }, clinicLinkId) {
      try {
            const response = await localServerHttp.get(
              `/userrecord/admin/getAllUserConfig?linkIds=${clinicLinkId}`
            )

        // console.log("response.data", response.data) 
        const users = response.data
        .filter(user => user.active)
        .map(user => ({
          id: user.id,
          email: user.email,
          active: user.active
        }))

        // console.log("mapped users", users) 
        commit("SET_USERS", users)
      } catch (error) {
        console.error("Error fetching users:", error.response?.data || error)
      }
    },
    async signupUser({state}, payload) {
      try {
        const response = await axios.post(
          `${state.authUrl}/admin/signup`, 
          // "http://localhost:8080/api/users/add",
          // "http://localhost:3000/api/auth/admin/signup",
          payload
        )
        // console.log("User saved to backend:", response.data)
        return response.data
      } catch (error) {
        console.error("Error saving user:", error)
        throw error
      }
    },
    async login({ commit, dispatch }, { email, password, captchaToken }) {
      try {
        // HEL-2250: use authHttp (withCredentials) so helen-auth Set-Cookie is stored
        // HEL-2291: always-on reCAPTCHA on helen-security Auth
        const body = { email, password };
        if (captchaToken) {
          body.captchaToken = captchaToken;
        }
        const response = await authHttp.post("admin/signin", body);
        commit("SET_USER_DATA", response.data);
        const isSuperAdmin = response.data.isSuperAdmin === true;
        commit("SET_IS_SUPER_ADMIN", isSuperAdmin);
        localStorage.setItem("isSuperAdmin", isSuperAdmin.toString());
          localStorage.setItem("link", response.data.link);
          commit("SET_ADMIN_LINK", response.data.link);
          localStorage.setItem("adminuserid", response.data.id);
        // HEL-2250: mint short-lived HelenAPI JWT into memory + LocalServer session cookies
        if (response.data.link) {
          try {
            const auth = await dispatch("checkAuth", response.data.link);
            if (auth && auth.token) {
              commit("SET_TOKEN", auth.token);
              await dispatch("establishAuthSession", {
                linkId: response.data.link,
                deviceLabel: "Helen admin",
              });
            }
          } catch (e) {
            console.warn("HEL-2250: admin JWT mint after login failed:", e);
          }
        }
        return response.data;
      } catch (error) {
        commit("SET_USER_DATA", null);
        commit("SET_IS_SUPER_ADMIN", false);
        throw error;
      }
    },
    /** HEL-2291: public captcha bootstrap (requiredAlways + site key). */
    fetchAuthCaptchaConfig() {
      return authHttp
        .get("captcha-config")
        .then((r) => r?.data || null)
        .catch(() => null);
    },
    checkAuth(state, id) {
      return new Promise((resolve, reject) => {
        // HEL-2280: mint via helen-auth session cookie — not Worldish UUID-only mint
        authHttp
          .get("links/login/" + id)
          .then((r) => {
            resolve(r.data);
          })
          .catch(() => {
            reject("Unauthorized");
          });
      });
    },
    async establishAuthSession({ state }, { linkId, deviceLabel } = {}) {
      const jwtToken = state.token || getMemoryAuthToken() || "";
      if (!jwtToken) {
        return;
      }
      try {
        await userRecordInstance.post(
          "/api/auth/session",
          {
            linkId: linkId || localStorage.getItem("link") || state.adminId || null,
            deviceLabel: deviceLabel || "Helen admin",
          },
          { headers: { Token: jwtToken } }
        );
      } catch (e) {
        console.warn("HEL-2250: admin auth session establish failed:", e?.message || e);
      }
    },
    async getClinics({ state, commit }, ) {
      try {
        const adminLink = localStorage.getItem("link");
        const mapClinics = (rows = []) =>
          rows.map(item => ({
          id: item.id,
          name: item.clinicName,
          clinicLinkId: item.clinicLinkId,
          adminLinkId: item.adminLinkId,
          }));

        let clinics = [];

        if (state.isSuperAdmin) {
          const allRes = await localServerHttp.get(`/adminview/all`);
          clinics = mapClinics(allRes.data);
        } else {
          if (!adminLink) {
            commit("SET_CLINICS", []);
            commit("SET_USERS_BY_LINK", {});
            return [];
          }

          // Primary production route for admin clinics
          const primaryRes = await localServerHttp.get(
            `/userrecord/admin/clinic/${adminLink}`
          );
          clinics = mapClinics(primaryRes.data);

          // Fallback route used by other admin flows
          if (clinics.length === 0) {
            const fallbackRes = await localServerHttp.get(`/clinic/${adminLink}`);
            clinics = mapClinics(fallbackRes.data);
          }
        }

        const linkIds = clinics
          .map(clinic => (clinic.clinicLinkId || "").trim())
          .filter(Boolean);
        if(linkIds.length === 0) {
          commit("SET_CLINICS", clinics);
          commit("SET_USERS_BY_LINK", {});
          return clinics;
        }
        try {
          await this.dispatch("getAllUsersByLinks", linkIds);
        } catch (userErr) {
          // Clinics still usable without per-clinic user counts
          console.warn("getAllUsersByLinks failed:", userErr);
          const emptyMap = {};
          linkIds.forEach(id => { emptyMap[id] = []; });
          commit("SET_USERS_BY_LINK", emptyMap);
        }
        commit("SET_CLINICS", clinics);
        return clinics;
      } catch (error) {
        commit("SET_CLINICS", []);
        commit("SET_USERS_BY_LINK", {});
        throw error;
      }
    },
    async getAllUsersByLinks({ commit }, linkIds) {
      try {
        // Build query string for multiple linkIds
        const params = linkIds.map(id => `linkIds=${id}`).join("&");
        const response = await localServerHttp.get(
          `/userrecord/admin/getAllUserConfig?${params}`
        );
        // Assume response.data is an array of user objects with linkId property
        const usersMap = {};
        linkIds.forEach(id => { usersMap[id] = []; });
        (response.data || []).forEach(user => {
          const lid = user.link;
          if (!lid) return;
          if (!usersMap[lid]) usersMap[lid] = [];
          usersMap[lid].push(user);
        });
        // console.log("Users by link:", usersMap);
        commit("SET_USERS_BY_LINK", usersMap);
        return usersMap;
      } catch (error) {
        const emptyMap = {};
        (linkIds || []).forEach(id => { emptyMap[id] = []; });
        commit("SET_USERS_BY_LINK", emptyMap);
        throw error;
      }
    },
  //   async uploadCSV(_, payload) {
  //   try {
  //     const formData = new FormData();

  //     const userData = {
  //       linkId: payload.link,
  //       clinicName: payload.clinicName,
  //       password: payload.password,
  //     };

  //     formData.append("file", payload.csvFile);

  //     formData.append(
  //       "userData",
  //       new Blob([JSON.stringify(userData)], { type: "application/json" })
  //     );

  //     // console.log("payload send to backend for bulk user sent:");
  //     for (let [key, value] of formData.entries()) {
  //       if (value instanceof File) {
  //         console.log(`${key}: File -> ${value.name}, size: ${value.size} bytes`);
  //       } else if (value instanceof Blob) {
  //         console.log(`${key}: Blob -> ${value.size} bytes`);
  //       } else {
  //         console.log(`${key}: ${value}`);
  //       }
  //     }

  //     const res = await axios.post(
  //       // "http://localhost:8080/api/users/bulk-users",
  //       `${this.state.authUrl}/batchUser`,
  //       // "https://auth.se-local-server-helen.com/api/auth/admin/batchUser",
  //       // "http://localhost:3000/api/auth/admin/batchUser",
        
  //       formData,
  //       { headers: { "Content-Type": "multipart/form-data" } }
  //     );
  //     return res.data;

  //   } catch (err) {
  //     console.error("error:", err);
  //     if (err.response && err.response.data) {
  //       return Promise.reject(err.response.data);
  //     }
  //     throw err;
  //   }
  // },
   
    async updateUserPassword(_, payload) {
      try {
        const response = await axios.put(
          `${this.state.authUrl}/admin/update-password`,
          // "https://auth.se-local-server-helen.com/api/auth/admin/update-password",
          // "http://localhost:8080/api/users/update-password",
          payload,
          // { headers: { "Content-Type": "application/json" } }
        );
        // HEL-2256: do not log password-update API responses
        return response.data;
      } catch (error) {
        console.error("Error updating password:", error.response?.data || error);
        throw error; 
      }
    },
    //deactivate users
    // async deleteUser(_, payload) {
    //   try {
    //     const response = await axios.put(
    //       `${this.state.authUrl}/deactivate-users`,
    //       // "https://auth.se-local-server-helen.com/api/auth/admin/deactivate-users",
    //       // "http://localhost:8080/api/users/deactivate-users",
    //       payload,
    //     );
    //     console.log("User deleted successfully:", response.data);
    //     return response.data;
    //   } catch (error) {
    //     console.error("Error while deletion:", error.response?.data || error);
    //     throw error; 
    //   }
    // },
    async deleteUser(_, payload) {
      try {
        const response = await axios.delete(
            `${this.state.authUrl}/admin/delete-users-with-mappings`,
          // "http://localhost:8080/api/users/delete-users-with-mappings",
          {
            data: payload,
            headers: { "Content-Type": "application/json" }
          }
        );
        console.log("User deleted successfully:", response.data);
        return response.data;
      } catch (error) {
        console.error("Error while deletion:", error.response?.data || error);
        throw error;
      }
    },

    deleteFolder({ commit }, folderId) {
    commit("REMOVE_FOLDER", folderId);
  },
      async uploadCSV({ state }, payload) {
      try {
        const formData = new FormData();
        const userData = {
          user: {
            linkId: payload.link,
            clinicName: payload.clinicName,
            password: payload.password,
            isDepartment: payload.isDepartment,
          },
          mappings: payload.mappings || [],
        };
        console.log("UserData being sent:", userData);
        formData.append("file", payload.csvFile);
        formData.append(
          "userData", // This should match what the backend expects
          new Blob([JSON.stringify(userData)], { type: "application/json" })
        );
        // console.log("payload send to backend for bulk user sent:");
        // for (let [key, value] of formData.entries()) {
        //   if (value instanceof File) {
        //     console.log(
        //       `${key}: File -> ${value.name}, size: ${value.size} bytes`
        //     );
        //   } else if (value instanceof Blob) {
        //     console.log(`${key}: Blob -> ${value.size} bytes`);
        //   } else {
        //     console.log(`${key}: ${value}`);
        //   }
        // }
        const res = await axios.post(
          `${state.authUrl}/batch-users-with-mappings`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        return res.data;
      } catch (err) {
        console.error("error:", err);
        if (err.response && err.response.data) {
          return Promise.reject(err.response.data);
        }
        throw err;
      }
    },
        async fetchUserWithMappings({ state }, email) {
      try {
        const response = await axios.get(
          `${state.authUrl}/user-with-mappings/${email}`
        );
        return response.data;
      } catch (error) {
        console.error(
          "Error fetching user details:",
          error.response?.data || error
        );
        throw error;
      }
    },

    // Sign up user with mappings
    async signupWithMappings({ state }, payload) {
      try {
        const response = await axios.post(
          `${state.authUrl}/admin/signup-with-mappings`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error(
          "Error creating user with mappings:",
          error.response?.data || error
        );
        throw error;
      }
    },

    // Update user categories and roles
    async updateUserCategoriesRoles({ state }, payload) {
      try {
        const response = await axios.put(
          `${state.authUrl}/admin/update-categories-and-roles`,
          payload
        );
        return response.data;
      } catch (error) {
        console.error(
          "Error updating user categories:",
          error.response?.data || error
        );
        throw error;
      }
    },
    async fetchCategories({ commit }) {
      try {
        const response = await localServerHttp.get(`/categories`);
        commit("SET_CATEGORIES", response.data);
        return response.data;
      } catch (error) {
        console.error(
          "Error fetching categories:",
          error.response?.data || error
        );
      }
    },
    async fetchRoleInstances(_, clinicLinkId) {
      try {
        const response = await userRecordInstance.get("/api/roleinstances", {
          params: { clinicLinkId }
        });
        return response.data;
      } catch (error) {
        console.error("Error fetching role instances:", error.response?.data || error);
      }
    },
    async fetchAllRoleInstances() {
      try {
        const response = await userRecordInstance.get("/api/roleinstances/all");
        return response.data;
      } catch (error) {
        console.error("Error fetching all role instances:", error.response?.data || error);
      }
    },
    async saveInstanceMap(_, instanceMapData) {
      try {
        const response = await userRecordInstance.put("/api/roleinstances", instanceMapData);
        return response.data;
      } catch (error) {
        console.error("Error saving instance map:", error.response?.data || error);
      }
    },
    async fetchInstances() {
      try {
        const response = await userRecordInstance.get("/api/instances", {
          params: { tag: "start" }
        });
        return response.data; // Returns array of instance IDs: [instanceid1, instanceid2, ...]
      } catch (error) {
        console.error("Error fetching instances:", error.response?.data || error);
      }
    },
    async fetchLinks() {
      try {
        const token = getMemoryAuthToken() || "";
        if (!token) {
          console.error("HEL-2250: No in-memory Token for GET /api/links");
          return [];
        }
        const response = await userRecordInstance.get("/api/links");
        return response.data; // Returns array of link objects with id and instance
      } catch (error) {
        console.error("Error fetching links:", error.response?.data || error);
        return [];
      }
    },

    /** HEL-2250: revoke LocalServer JWT so it cannot be replayed after logout. */
    async revokeTokenOnServer({ state }) {
      const jwtToken = state.token || getMemoryAuthToken() || "";
      if (!jwtToken) {
        return;
      }
      try {
        // userRecordInstance base is LocalServer host; path includes /api
        await userRecordInstance.post(
          "/api/auth/logout",
          {},
          { headers: { Token: jwtToken } }
        );
      } catch (e) {
        console.warn(
          "HEL-2250: server token revoke failed (continuing local logout):",
          e?.message || e
        );
      }
    },

    /** HEL-2250: revoke JWT then clear admin session keys. */
    async forceSessionLogout({ dispatch, commit, state }) {
      await dispatch("revokeTokenOnServer");
      try {
        // HEL-2250: clear helen-auth HttpOnly session cookie
        await authHttp.post("signout");
      } catch (e) {
        console.warn("HEL-2250: auth cookie signout failed:", e?.message || e);
      }
      if (state.inactivityTimer?.timerId) {
        clearInterval(state.inactivityTimer.timerId);
      }
      commit("STOP_ACTIVITY_TIMER");
      commit("SET_TOKEN", null);
      localStorage.removeItem("token");
      localStorage.removeItem("adminuserid");
      localStorage.removeItem("link");
      localStorage.removeItem("isSuperAdmin");
      localStorage.removeItem("instance");
      commit("SET_IS_SUPER_ADMIN", false);
    },

    /** HEL-2250 / HEL-2286: admin idle timeout → forced re-auth. */
    startActivityTimer({ commit, dispatch, state }, onTimeout) {
      if (state.inactivityTimer.timerId) {
        clearInterval(state.inactivityTimer.timerId);
      }
      const timerId = setInterval(() => {
        const start = state.inactivityTimer.startDate;
        if (!start) return;
        const elapsed = (Date.now() - start) / 1000;
        if (elapsed >= state.inactivityTimer.maxTimer) {
          if (typeof state.inactivityTimer.onTimeout === "function") {
            state.inactivityTimer.onTimeout();
          } else if (typeof onTimeout === "function") {
            onTimeout();
          } else {
            dispatch("forceSessionLogout").then(() => {
              if (typeof window !== "undefined") {
                window.location.href = "/";
              }
            });
          }
        }
      }, 5000);
      commit("START_ACTIVITY_TIMER", {
        timerId,
        onTimeout:
          onTimeout ||
          (() =>
            dispatch("forceSessionLogout").then(() => {
              if (typeof window !== "undefined") {
                window.location.href = "/";
              }
            })),
      });
    },
    resetActivityTimer({ commit }) {
      commit("RESET_ACTIVITY_TIMER");
    },
    stopActivityTimer({ commit, state }) {
      if (state.inactivityTimer.timerId) {
        clearInterval(state.inactivityTimer.timerId);
      }
      commit("STOP_ACTIVITY_TIMER");
    },

  },

  getters: {
    userData: state => state.userData,
    clinics: state => state.clinics || [],
    usersByLink: state => state.usersByLink,
    getClinics: (state) => state.clinics,
    getUsers: state => state.users,
    getAllUsers: state => state.allUsers,
    folders: (state) => state.folders,
    getAdminId: state => state.adminId,
    isSuperAdmin: state => state.isSuperAdmin,
    clinicsInFolder: (state) => (folderId) => {
    const clinicIds = state.folderClinics[folderId] || [];
    return state.clinics.filter(c => clinicIds.includes(c.id));
  }
  },
})
